import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import {
  ShieldCheck,
  TrendingUp,
  Compass,
  FileBadge,
  Users,
  BookOpen,
  type LucideIcon,
} from "lucide-react";

export const Route = createFileRoute("/tools/legacy-score/")({
  head: () => ({
    meta: [
      { title: "The Legacy Score — A 100-Point Family Readiness Framework" },
      {
        name: "description",
        content:
          "Six dimensions. One hundred points. A clear picture of your family's legacy readiness — and the exact next steps to improve it.",
      },
      {
        property: "og:title",
        content: "The Legacy Score — Currency Thoughts",
      },
      {
        property: "og:description",
        content:
          "A 10-minute, 100-point assessment of how prepared your family is to receive, protect, and grow what you've built.",
      },
    ],
    links: [{ rel: "canonical", href: "/tools/legacy-score" }],
  }),
  component: LegacyScoreIntroPage,
});

type DimensionCard = {
  icon: LucideIcon;
  name: string;
  description: string;
};

const DIMENSIONS: DimensionCard[] = [
  {
    icon: ShieldCheck,
    name: "Protection",
    description: "Life coverage adequacy for income, debt, and education.",
  },
  {
    icon: TrendingUp,
    name: "Income Continuity",
    description: "Disability, emergency reserves, and key-person coverage.",
  },
  {
    icon: Compass,
    name: "Retirement Readiness",
    description: "Surviving-spouse income continuity and longevity plan.",
  },
  {
    icon: FileBadge,
    name: "Estate Documents",
    description: "Will, Trust, financial POA, and healthcare directive.",
  },
  {
    icon: Users,
    name: "Beneficiaries",
    description: "Named, current, and properly structured.",
  },
  {
    icon: BookOpen,
    name: "Family Education",
    description: "Next generation prepared to receive what you've built.",
  },
];

function LegacyScoreIntroPage() {
  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* HERO */}
      <section className="bg-navy-900 text-cream-50">
        <FiligreeFrame intensity="subtle">
          <div className="mx-auto max-w-5xl px-6 py-24 md:py-32">
            <Eyebrow color="cream">Introducing</Eyebrow>
            <h1 className="mt-6 font-display font-bold text-cream-50 text-[44px] leading-[1.05] md:text-[72px]">
              The{" "}
              <em className="text-bronze-400 italic not-italic md:italic">
                Legacy Score.
              </em>
            </h1>
            <p className="mt-8 max-w-[560px] font-sans text-[17px] md:text-[18px] leading-[1.65] text-cream-50/85">
              Six dimensions. One hundred points. A clear picture of your
              family's legacy readiness — and the exact next steps to improve
              it.
            </p>
            <div className="mt-10">
              <PrimaryButton asChild size="lg">
                <Link to="/tools/legacy-score/assess">
                  Take the Assessment (10 min) →
                </Link>
              </PrimaryButton>
            </div>
            <p className="mt-5 font-sans text-[12px] uppercase tracking-[0.18em] text-cream-50/60">
              1,000+ families assessed · Free forever · Update yearly
            </p>
          </div>
        </FiligreeFrame>
      </section>

      {/* SIX DIMENSIONS */}
      <section className="bg-cream-50 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex justify-center">
            <Eyebrow color="bronze">The Framework</Eyebrow>
          </div>
          <h2 className="mt-4 text-center font-display font-semibold text-navy-900 text-[36px] md:text-[44px] leading-[1.1]">
            Six dimensions of legacy.
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-center font-sans text-[16px] text-charcoal-500 leading-[1.7]">
            Each dimension is scored from 0 to 100. Together they roll up into
            your Legacy Score — and a roadmap to raise it.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {DIMENSIONS.map((d) => {
              const Icon = d.icon;
              return (
                <div
                  key={d.name}
                  className="relative bg-white border border-bronze-600/30 p-8 rounded-[4px]"
                  style={{ boxShadow: "var(--shadow-soft)" }}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy-900 ring-2 ring-bronze-600/60">
                    <Icon className="h-6 w-6 text-bronze-400" aria-hidden />
                  </div>
                  <h3 className="mt-6 font-display font-semibold text-navy-900 text-[22px] leading-tight">
                    {d.name}
                  </h3>
                  <p className="mt-3 font-sans text-[14px] text-charcoal-500 leading-[1.65]">
                    {d.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Eyebrow color="bronze" align="center">
            The Methodology
          </Eyebrow>
          <h2 className="mt-5 font-display font-semibold italic text-navy-900 text-[32px] md:text-[40px] leading-[1.15]">
            "What gets measured gets built."
          </h2>
          <p className="mx-auto mt-7 max-w-[640px] font-sans text-[17px] text-charcoal-900 leading-[1.75]">
            Most families never get a complete picture of where they stand.
            Coverage lives with one carrier, documents with an attorney,
            retirement with another strategist, and beneficiaries on a form no
            one has reviewed in a decade. The Legacy Score brings the whole
            picture onto a single page — in ten minutes — and shows you the
            three highest-leverage actions to take next. No pitch. No login.
            Just clarity.
          </p>
          <div className="mt-10">
            <PrimaryButton asChild size="lg">
              <Link to="/tools/legacy-score/assess">
                Take the Assessment →
              </Link>
            </PrimaryButton>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}